import React from "react";
import GridCellCropping from "./GridCellCropping";
import { connect } from "react-redux";
import {
  isCroppingSelector,
  contextSelector,
  selectedItemsSelector,
  zoomSelector,
  designDataSelector,
  elementPreviewsSelector
} from "state/ui/editorContext/editorContextSelectors";
import { getPageOffsetInCanvasbackground } from "views/components/Editor/utils";
import { update as updateEditorContextState } from "state/ui/editorContext/editorContextActions";
import EditorGridOps from "views/components/Editor/editorOps/EditorGridOps";
import { setInnerImageRotation } from "state/ui/editor/innerImageRotationUtils";
import { Logger } from "lib";

export class GridCellCroppingContainer extends React.Component {
  constructor(props) {
    super(props);

    this.startGridCellCropMoving = this.startGridCellCropMoving.bind(this);
    this.finishGridCellCropMoving = this.finishGridCellCropMoving.bind(this);
    this.handleInnerImageRotationCommit = this.handleInnerImageRotationCommit.bind(
      this
    );
    this.handleInnerImageRotationPreview = this.handleInnerImageRotationPreview.bind(
      this
    );
    this.pendingPreviewRotation = null;
    this.previewRafId = null;
  }

  componentWillUnmount() {
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
  }

  // Preview ticks fire on every drag move (mouse / touch / slider). Without
  // coalescing, each tick clones selectedItems and dispatches updateContext
  // through redux — visibly janky on larger designs. Throttling to one
  // dispatch per animation frame caps the redux churn at the display rate
  // while keeping the visual rotation feedback smooth. The commit path
  // (mouseup) bypasses this and writes immediately so the final value is
  // never a frame stale. Mirrors PhotoFrameCroppingContainer.
  handleInnerImageRotationPreview(rotation) {
    this.pendingPreviewRotation = rotation;
    if (this.previewRafId !== null) return;
    this.previewRafId = requestAnimationFrame(() => {
      this.previewRafId = null;
      const next = this.pendingPreviewRotation;
      this.pendingPreviewRotation = null;
      if (next === null || next === undefined) return;
      this.handleInnerImageRotationCommit(next);
    });
  }

  handleInnerImageRotationCommit(rotation) {
    // Cancel any preview rAF still queued from the same drag — otherwise
    // the queued frame fires after this synchronous commit and dispatches
    // a redundant updateContext (idempotent today thanks to lastRotationRef
    // in InnerImageRotateHandle, but still costs a full selectedItems
    // clone + redux roundtrip per drag and would mask any future "snap to"
    // commit value that didn't match the last preview). Mirrors the fix
    // applied in PhotoFrameCroppingContainer.
    if (this.previewRafId !== null) {
      cancelAnimationFrame(this.previewRafId);
      this.previewRafId = null;
    }
    this.pendingPreviewRotation = null;

    // Preview and commit go through the same path because the grid-cell
    // crop flow is preview-only until the user clicks apply crop — at
    // which point updateGridImageInstructionsFromSelectedItems spreads
    // selectedItems[0].preview onto designData.
    const { selectedItems, designData, context, updateContext } = this.props;
    const { selectedGridCellId } = context;

    if (!selectedItems[0] || !selectedGridCellId) return;

    const element = designData.getElement(selectedItems[0].itemId);
    if (!element) return;

    const existingPreview = selectedItems[0].preview || {};
    const baseInstructions =
      existingPreview.imageInstructions || element.imageInstructions || [];

    // Pass the actual element (with preview-merged instructions overlaid)
    // so element.type drives the branch in setInnerImageRotation. A
    // synthetic { type: "vector" } literal would silently fall through
    // for grid elements.
    const { imageInstructions: updatedInstructions } = setInnerImageRotation(
      { ...element, imageInstructions: baseInstructions },
      selectedGridCellId,
      rotation
    );

    const updatedSelectedItems = selectedItems.map((item, index) =>
      index === 0
        ? {
            ...item,
            preview: {
              ...existingPreview,
              imageInstructions: updatedInstructions
            }
          }
        : item
    );

    updateContext({ selectedItems: updatedSelectedItems });
  }

  startGridCellCropMoving() {
    Logger.info("GridCellCroppingContainer.startGridCellCropMoving called");

    this.props.updateContext({
      isCropping: true
    });
  }

  finishGridCellCropMoving({ differenceFromInitialOffset }) {
    Logger.info("GridCellCroppingContainer.finishGridCellCropMoving called");
    const {
      zoom,
      selectedItems,
      designData,
      context: { selectedGridCellId, gridCellHtmlElement },
      updateContext
    } = this.props;

    const { gridElement, gridCell } = EditorGridOps.getGridElementWithPreview({
      selectedItems,
      designData,
      selectedGridCellId
    });

    const gridCellIndex = gridElement.imageInstructions.findIndex(
      imageInstruction => imageInstruction.domId === selectedGridCellId
    );

    const updatedContext = EditorGridOps.finishGridCellCropMoving({
      differenceFromInitialOffset,
      zoom,
      selectedItems,
      gridElement,
      gridCell,
      gridCellHtmlElement,
      gridCellIndex
    });

    updateContext(updatedContext);
  }

  render() {
    const {
      context: {
        isCroppingGridCell,
        selectedGridCellId,
        gridHtmlElement,
        gridCellHtmlElement
      },
      designData,
      selectedItems,
      zoom,
      canvasBackground,
      isCropping
    } = this.props;

    if (!canvasBackground || !isCroppingGridCell) {
      return null;
    }

    const { gridElement, gridCell } = EditorGridOps.getGridElementWithPreview({
      selectedGridCellId,
      selectedItems,
      designData
    });

    const pageId = selectedItems[0].pageId;
    const pageOffset = getPageOffsetInCanvasbackground(pageId);

    return (
      <GridCellCropping
        {...this.props}
        gridHtmlElement={gridHtmlElement}
        gridCellHtmlElement={gridCellHtmlElement}
        startGridCellCropMoving={this.startGridCellCropMoving}
        finishGridCellCropMoving={this.finishGridCellCropMoving}
        onInnerImageRotationPreview={this.handleInnerImageRotationPreview}
        onInnerImageRotationCommit={this.handleInnerImageRotationCommit}
        gridCell={gridCell}
        gridElement={gridElement}
        pageOffset={pageOffset}
        zoom={zoom}
        canvasBackground={canvasBackground}
        isCropping={isCropping}
      />
    );
  }
}

const mapStateToProps = state => {
  return {
    isCropping: isCroppingSelector(state),
    context: contextSelector(state),
    selectedItems: selectedItemsSelector(state),
    zoom: zoomSelector(state),
    elementPreviews: elementPreviewsSelector(state),
    designData: designDataSelector(state)
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateContext: args => dispatch(updateEditorContextState(args))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GridCellCroppingContainer);
